import { Copy, Repeat2, Share2, X } from 'lucide-react-native';
import { useEffect, useRef, type ReactNode } from 'react';
import {
  Animated,
  Easing,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

export type ShareOptionKind = 'copy' | 'facebook' | 'native' | 'reshare';

export type ShareOption = {
  description: string;
  disabled?: boolean;
  icon?: ReactNode;
  kind: ShareOptionKind;
  loading?: boolean;
  loadingTitle?: string;
  onPress: () => void;
  title: string;
};

function defaultIcon(kind: ShareOptionKind) {
  if (kind === 'copy') {
    return <Copy color={colors.secondary} size={20} strokeWidth={2.5} />;
  }

  if (kind === 'facebook') {
    return <Text style={styles.facebookShareMark}>f</Text>;
  }

  if (kind === 'reshare') {
    return <Repeat2 color={colors.secondary} size={20} strokeWidth={2.5} />;
  }

  return <Share2 color={colors.secondary} size={20} strokeWidth={2.5} />;
}

export function ShareOptionsModal({
  busy,
  onClose,
  options,
  subtitle,
  title,
  visible,
}: {
  busy?: boolean;
  onClose: () => void;
  options: ShareOption[];
  subtitle: string;
  title: string;
  visible: boolean;
}) {
  const sheetProgressRef = useRef(new Animated.Value(0));
  const sheetProgress = sheetProgressRef.current;

  useEffect(() => {
    if (!visible) {
      sheetProgress.setValue(0);
      return;
    }

    Animated.timing(sheetProgress, {
      duration: 190,
      easing: Easing.out(Easing.cubic),
      toValue: 1,
      useNativeDriver: true,
    }).start();
  }, [sheetProgress, visible]);

  const modalMotionStyle = {
    opacity: sheetProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [0.88, 1],
    }),
    transform: [
      {
        translateY: sheetProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [24, 0],
        }),
      },
      {
        scale: sheetProgress.interpolate({
          inputRange: [0, 1],
          outputRange: [0.985, 1],
        }),
      },
    ],
  };

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <Pressable
        style={styles.backdrop}
        disabled={busy}
        onPress={onClose}
      >
        <Animated.View style={[styles.modal, modalMotionStyle]}>
        <Pressable style={styles.modalPressable} onPress={(event) => event.stopPropagation()}>
          <View style={styles.header}>
            <View style={styles.titleBlock}>
              <Text style={styles.title}>{title}</Text>
              <Text style={styles.subtitle}>{subtitle}</Text>
            </View>
            <Pressable
              accessibilityLabel="Close share options"
              accessibilityRole="button"
              disabled={busy}
              onPress={onClose}
              style={({ pressed }) => [
                styles.closeButton,
                pressed && !busy ? styles.closeButtonPressed : null,
              ]}
            >
              <X color={colors.mutedText} size={20} strokeWidth={2.5} />
            </Pressable>
          </View>
          <View style={styles.optionList}>
            {options.map((option) => {
              const disabled = busy || option.disabled;
              const loading = Boolean(option.loading);

              return (
                <Pressable
                  key={option.kind}
                  accessibilityLabel={option.title}
                  accessibilityRole="button"
                  disabled={disabled}
                  onPress={option.onPress}
                  style={({ pressed }) => [
                    styles.option,
                    pressed && !disabled ? styles.optionPressed : null,
                    disabled && !loading ? styles.optionDisabled : null,
                  ]}
                >
                  <View style={styles.optionIcon}>
                    {option.icon ?? defaultIcon(option.kind)}
                  </View>
                  <View style={styles.optionText}>
                    <Text style={styles.optionTitle}>
                      {loading
                        ? option.loadingTitle ?? 'Working...'
                        : option.title}
                    </Text>
                    <Text style={styles.optionDescription}>
                      {option.description}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </Pressable>
        </Animated.View>
      </Pressable>
    </Modal>
  );
}

export function nativeShareTitle() {
  return Platform.OS === 'web' ? 'Share with browser' : 'Share';
}

export function nativeShareDescription() {
  return Platform.OS === 'web'
    ? 'Use the browser share sheet when available.'
    : 'Use your device share sheet.';
}

const styles = StyleSheet.create({
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 12, 62, 0.28)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  modal: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    gap: spacing.lg,
    maxWidth: 430,
    padding: spacing.lg,
    shadowColor: '#000000',
    shadowOffset: { height: 10, width: 0 },
    shadowOpacity: 0.16,
    shadowRadius: 24,
    width: '100%',
  },
  modalPressable: {
    gap: spacing.lg,
  },
  header: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  titleBlock: {
    flex: 1,
    gap: 3,
  },
  title: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  subtitle: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
  },
  closeButton: {
    alignItems: 'center',
    borderRadius: 18,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  closeButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  optionList: {
    gap: spacing.xs,
  },
  option: {
    alignItems: 'center',
    borderRadius: 8,
    flexDirection: 'row',
    gap: spacing.md,
    minHeight: 58,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  optionDisabled: {
    opacity: 0.5,
  },
  optionIcon: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 19,
    borderWidth: 1,
    height: 38,
    justifyContent: 'center',
    width: 38,
  },
  optionPressed: {
    backgroundColor: colors.surfacePressed,
  },
  optionText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  optionTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  optionDescription: {
    color: colors.mutedText,
    fontSize: 12,
    lineHeight: 17,
  },
  facebookShareMark: {
    color: '#1877f2',
    fontSize: 22,
    fontWeight: '900',
    lineHeight: 24,
  },
});
